import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { downloadVideo, handleDownloadError } from "@src/features/clips";
import { showErrorToast } from "@src/utils/toast";
import type { TimelapseExportState } from "../types";
import { computeDefaultTimelapseRange } from "../utils/date-range";
import { subtitleForState } from "./subtitle-for-state";
import { useTimelapseExport } from "./useTimelapseExport";

export interface UseTimelapseButtonActionParams {
  readonly siteId: string;
  readonly cameraKey: string;
  readonly cameraTimezone: string;
  readonly enabled?: boolean;
}

export interface UseTimelapseButtonActionResult {
  readonly subtitle: string;
  readonly state: TimelapseExportState;
  readonly cameraEligible: boolean;
  readonly isBusy: boolean;
  readonly isActionable: boolean;
  readonly onPress: () => Promise<void>;
}

function isHttpStatusError(error: unknown, status: number): boolean {
  if (typeof error !== "object" || error === null) return false;
  const candidate = error as { status?: unknown };
  return candidate.status === status;
}

function isTimelapseReRequestWindowError(error: unknown): error is Error {
  return error instanceof Error && /once per hour/i.test(error.message);
}

export function useTimelapseButtonAction(params: UseTimelapseButtonActionParams): UseTimelapseButtonActionResult {
  const { siteId, cameraKey, cameraTimezone, enabled = false } = params;
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasDownloadedReadyVideo, setHasDownloadedReadyVideo] = useState(false);
  const inFlightPressRef = useRef(false);

  const range = useMemo(() => computeDefaultTimelapseRange(new Date(), cameraTimezone), [cameraTimezone]);
  const exportApi = useTimelapseExport({ siteId, cameraKey, cameraTimezone, range, enabled });
  const exportState = exportApi.state;
  const requestExport = exportApi.requestExport;
  const exportStateKind = exportState.kind;
  const readyDownloadUrl = exportStateKind === "ready" ? exportState.downloadUrl : null;

  const isActionable =
    enabled && (exportStateKind === "idle" || exportStateKind === "ready" || exportStateKind === "failed");

  useEffect(() => {
    if (exportStateKind !== "ready") {
      setHasDownloadedReadyVideo(false);
    }
  }, [exportStateKind]);

  const onPress = useCallback(async () => {
    if (!isActionable || inFlightPressRef.current) return;
    const downloadUrl = exportStateKind === "ready" ? readyDownloadUrl : null;
    const isDownloadAction = downloadUrl !== null;

    try {
      inFlightPressRef.current = true;
      setIsDownloading(true);
      if (downloadUrl) {
        await downloadVideo({
          videoUrl: downloadUrl,
          timestamp: new Date(range.endTime),
        });
        setHasDownloadedReadyVideo(true);
      } else {
        setHasDownloadedReadyVideo(false);
        await requestExport();
      }
    } catch (error: unknown) {
      if (isDownloadAction) {
        handleDownloadError(error);
      } else {
        if (isHttpStatusError(error, 403)) {
          // Eligibility can change between initial status check and click.
          // Treat denied export requests as a silent feature-gate outcome.
          return;
        }
        if (isTimelapseReRequestWindowError(error)) {
          showErrorToast({ message: error.message });
          return;
        }
        console.error("Timelapse export request error:", error);
        showErrorToast({
          error,
          description: "Could not start timelapse export. Please try again.",
        });
      }
    } finally {
      inFlightPressRef.current = false;
      setIsDownloading(false);
    }
  }, [isActionable, exportStateKind, readyDownloadUrl, range.endTime, requestExport]);

  return {
    subtitle: subtitleForState(exportState, hasDownloadedReadyVideo),
    state: exportState,
    cameraEligible: exportApi.cameraEligible,
    isBusy:
      isDownloading ||
      exportStateKind === "requesting" ||
      exportStateKind === "queued" ||
      exportStateKind === "processing",
    isActionable,
    onPress,
  };
}
